<template>
  <div @close.stop>
    <div class="user-grid">
      <ngc-input-text
        label="First Name"
        placeholder="Enter first name"
        :value="firstName"
        required
        :max-length="50"
        @input="firstName = $event.detail.value"
      />
      <ngc-input-text
        label="Last Name"
        placeholder="Enter last name"
        :value="lastName"
        required
        :max-length="50"
        @input="lastName = $event.detail.value"
      />
      <ngc-input-text
        label="Email"
        placeholder="Enter email"
        :value="email"
        required
        :disabled="edit"
        :error-text="emailError"
        @input="email = $event.detail.value"
        @change="getExistingUser"
      />
      <ngc-input-dropdown
        label="Account Type"
        placeholder="Select a role for this user"
        :options.prop="roleTypeOptions"
        :value.prop="roleType"
        @change="roleType = $event.detail.value"
      />
      <ngc-input-dropdown
        label="Time Zone"
        placeholder="Select a preferred time zone for this user"
        :options.prop="timeZoneDropdownOptions"
        :value.prop="timeZone"
        @change="timeZone = $event.detail.value"
      />
      <env-select
        label="Environments"
        :value="selectedEnvironments"
        :items="environmentOptions"
        :disabled="true"
        placeholder="Inherited from account type"
      />
    </div>

    <div
      v-if="!isSnAdminCompany"
      class="user-orgs"
    >
      <ngc-input-label class="contact-label">
        Org IDs
      </ngc-input-label>
      <mers-org-combobox
        v-model="selectedOrgs"
        class="orgs"
        :mers-orgs="orgs"
        :disabled="orgIdsDisabled"
        assign-dropdown-ref="userInfoOrgsCombobox"
      />
    </div>

    <div
      v-if="edit"
      class="user-actions-links"
    >
      <ngc-button
        v-if="lastLoginAt == null"
        variant="base"
        data-cy="resend-invite"
        :disabled="loading"
        @click="resendInvite"
      >
        Resend Invite
      </ngc-button>
      <ngc-button
        variant="base"
        data-cy="password-reset"
        :disabled="passwordResetLoading"
        @click="showPasswordResetModal = true"
      >
        Require password reset on next login
      </ngc-button>
    </div>

    <ngc-modal
      :open="showPasswordResetModal"
      variant="decide"
      :title.prop="'Require password reset on next login'"
      data-cy="confirmation-modal"
      primary-action-text="Require Reset"
      :loading="passwordResetLoading"
      @primary-action-click="forcePasswordReset"
      @secondary-action-click="showPasswordResetModal = false"
      @close="showPasswordResetModal = false"
    >
      <span>{{ passwordResetUserName }} will be required to set a new password the next time they log in. This is a one-time action.</span>
    </ngc-modal>
  </div>
</template>

<script>

import mersOrgCombobox from './mersOrgCombobox.vue'
import envSelect from './envSelect.vue'

import snEvaultService, { TIMEZONES } from '../../services/snEvaultService'
import { rolesPermissions } from '../graphql/companies_queries.gql'
import { user, editUserMutation } from '../graphql/users/users_queries.gql'
import { company, inviteUser, updateUsersForcePasswordReset } from '../graphql/users/user_access.gql'
import { mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'

export default {
  name: 'UserInfo',
  components: {
    envSelect,
    mersOrgCombobox
  },
  props: {
    edit: {
      type: Boolean,
      required: true,
      default: false
    },
    user: {
      type: Object,
      required: false,
      default: () => {}
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    },
    userGuid: {
      type: String,
      default: '',
      required: false
    }
  },
  data () {
    return {
      dbFirstName: this.user?.firstName || '',
      firstName: '',
      dbLastName: this.user?.lastName || '',
      lastName: '',
      dbTimeZone: this.user?.timezone || '',
      timeZone: '',
      defaultTimeZone: '',
      dbEmail: this.user?.email || '',
      email: '',
      emailErrors: [],
      emailAlreadyInUse: false,
      dbRoleType: this.user?.roleType || '',
      roleType: '',
      roleTypes: [],
      orgs: [],
      dbOrgs: [],
      selectedOrgs: [],
      companyMersOrgCount: 0,
      timeZoneOptions: TIMEZONES.map(tz => tz.label),
      environmentOptions: ['Staging', 'Production'],
      validTimeZone: true,
      validRoleType: true,
      roleEnvironments: {},
      loading: false,
      saveBtnLoading: false,
      initialLoad: true,
      passwordResetLoading: false,
      showPasswordResetModal: false,
      companyName: ''
    }
  },
  computed: {
    ...mapGetters({
      loggedInUser: 'user',
      mersOrgId: 'mersOrgId'
    }),
    roleTypeOptions () {
      return this.roleTypes.map(role => ({ label: role, value: role }))
    },
    timeZoneDropdownOptions () {
      return this.timeZoneOptions.map(tz => ({ label: tz, value: tz }))
    },
    selectedEnvironments () {
      if (this.roleType) {
        return this.roleEnvironments[this.roleType] || []
      }
      return []
    },
    limit50Chars () {
      return (input) => input && input?.length >= 50 ? '50 character limit' : true
    },
    notEmpty () {
      return (input) => input && input?.length > 0 ? true : 'Required field'
    },
    validateEmail () {
      return (input) => input ? snEvaultService.validateEmail(input) ? true : 'Invalid email format' : false
    },
    emailError () {
      if (this.emailErrors.length > 0) {
        return this.emailErrors[0]
      }
      if (!this.email) {
        return ''
      }
      return this.validateEmail(this.email) === true ? '' : 'Invalid email format'
    },
    dbValues () {
      return { firstName: this.dbFirstName, lastName: this.dbLastName, timeZone: this.dbTimeZone, email: this.dbEmail, roleType: this.dbRoleType, selectedOrgGuids: this.dbOrgs?.map(org => org.guid) }
    },
    // Saved name, not the edited form values - the reset applies to the user as
    // they exist on the server, and unsaved edits shouldn't change the wording.
    passwordResetUserName () {
      const name = `${this.dbFirstName || ''} ${this.dbLastName || ''}`.trim()
      return name || 'This user'
    },
    currentValues () {
      const { firstName, lastName, timeZone, email, roleType, selectedOrgGuids } = this
      return { firstName, lastName, timeZone, email, roleType, selectedOrgGuids }
    },
    selectedOrgGuids () {
      return this.selectedOrgs.map(org => org.guid)
    },
    managesUsers () {
      return snEvaultService.hasPermission(this.loggedInUser, 'manageUsers')
    },
    isEditingSelf () {
      if (this.edit) {
        return this.loggedInUser.guid === this.userGuid
      }
      return false
    },
    orgIdsDisabled () {
      if (this.edit) {
        return !this.managesUsers || this.isEditingSelf
      }
      return false
    },
    hasChanges () {
      if (this.edit) {
        return !_.isEqual(this.dbValues, this.currentValues)
      }
      return true
    },
    dataEntryComplete () {
      return this.selectedEnvironments?.length > 0 &&
        (this.isSnAdminCompany || this.selectedOrgs?.length > 0) &&
        this.validateEmail(this.email) === true &&
        this.limit50Chars(this.firstName) === true &&
        this.limit50Chars(this.lastName) === true &&
        [this.firstName, this.lastName, this.email, this.timeZone, this.roleType].every(val => Boolean(val)) &&
        !this.emailAlreadyInUse &&
        this.validTimeZone &&
        this.validRoleType
    },
    saveBtnDisabled () {
      return !this.hasChanges || !this.dataEntryComplete
    },
    enabledEnvironments () {
      return this.roleEnvironments[this.roleType]
    },
    lastLoginAt () { return this.user?.lastLoginAt },
    isSnAdminCompany () { return this.user?.company?.name === 'SnAdmin Company' || this.companyName === 'SnAdmin Company' }
  },
  watch: {
    dataEntryComplete (val) {
      this.$emit('validation', val)
    },
    saveBtnDisabled: {
      immediate: true,
      handler (val) {
        this.$emit('save-disabled', val)
      }
    },
    saveBtnLoading (val) {
      this.$emit('saving', val)
    },
    currentValues: {
      handler () {
        if (this.initialLoad) {
          this.initialLoad = false
          return
        }
        if (this.hasChanges) {
          this.$emit('change', this.currentValues)
        } else {
          this.$emit('change', {})
        }
      },
      deep: true
    },
    hasChanges (val) {
      this.$emit('changed', val)
    },
    user () {
      if (this.user) {
        this.setUserInfo()
      }
    },
    userGuid () {
      if (this.userGuid) {
        this.setUserInfo()
        this.getRoleEnvironments()
        this.getCompany()
      }
    },
    companyGuid () {
      if (this.companyGuid) {
        this.getCompany()
        this.getRoleEnvironments()
      }
    }
  },
  created () {
    this.setUserInfo()
    if (this.companyGuid) {
      this.getCompany()
      this.getRoleEnvironments()
    }
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'resetAppTimeout']),
    getCompany () {
      this.$apollo.query({
        query: company,
        fetchPolicy: 'no-cache',
        variables: {
          companyGuid: this.companyGuid
        }
      }).then(({ data: { company } }) => {
        this.companyName = company.name
        if (company) {
          this.orgs = company.orgs
          this.roleTypes = company.roleTypes.filter(role => role !== 'Support Admin')

          if (!this.edit) {
            const localTimeZone = TIMEZONES.find(tz => tz.value === Intl.DateTimeFormat().resolvedOptions().timeZone)
            this.timeZone = localTimeZone == null ? this.timeZoneOptions[0] : localTimeZone.label // if local time is not in list, default to UTC
            this.defaultTimeZone = localTimeZone == null ? this.timeZoneOptions[0] : localTimeZone.label // if local time is not in list, default to UTC

            if (this.roleTypes.length > 0) {
              this.roleType = this.roleTypes[0]
            }
          }
        }
      })
    },
    getRoleEnvironments () {
      this.$apollo.query({
        query: rolesPermissions,
        variables: {
          companyGuid: this.companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const { nodes } = data.roles
        const envMap = {}
        nodes.map(node => {
          const flatPermissions = JSON.parse(node.flatPermissions)
          if (flatPermissions.permission_setting_staging_enabled) {
            if (flatPermissions.permission_setting_production_enabled) {
              envMap[node.name] = ['Production', 'Staging']
            } else {
              envMap[node.name] = ['Staging']
            }
          } else if (flatPermissions.permission_setting_production_enabled) {
            envMap[node.name] = ['Production']
          }
        })
        this.roleEnvironments = envMap
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err.message}` })
      })
    },
    setUserInfo () {
      if (!this.user) {
        return
      }
      this.firstName = this.user.firstName
      this.lastName = this.user.lastName
      this.timeZone = this.user.timezone
      this.roleType = this.user.roleType
      this.email = this.user.email
      this.selectedOrgs = this.user.selectedOrgs || []

      this.dbFirstName = this.user.firstName
      this.dbLastName = this.user.lastName
      this.dbTimeZone = this.user.timezone
      this.dbEmail = this.user.email
      this.dbRoleType = this.user.roleType
      this.dbOrgs = this.selectedOrgs
    },
    async getExistingUser () {
      if (this.edit) {
        return
      }
      const { data } = await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          email: this.email
        }
      })
      if (data.user) {
        this.emailErrors = ['Email already in use']
        this.emailAlreadyInUse = true
      } else {
        this.emailErrors = []
        this.emailAlreadyInUse = false
      }
    },
    resendInvite () {
      this.loading = true
      const { firstName, lastName, email, timeZone, stagingEnabled, productionEnabled, roleType } = this.user
      this.$apollo.mutate({
        mutation: inviteUser,
        variables: {
          orgGuids: this.selectedOrgGuids,
          firstName,
          lastName,
          email,
          timeZone,
          stagingEnabled,
          productionEnabled,
          roleType,
          companyGuid: this.companyGuid,
          newInvite: false
        }
      }).then(({ data }) => {
        this.loading = false
        if (data.inviteUser.error) {
          this.setErrorToastType({ description: data.inviteUser.error || 'There was an error with the request' })
          this.resendInviteHadErrors = true
        } else {
          this.setSuccessToast({ description: 'Invitation sent' })
        }
        this.resetAppTimeout()
      }).catch(err => {
        this.loading = false
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
        this.resendInviteHadErrors = true
      })
    },
    handleSave () {
      this.saveBtnLoading = true
      const { firstName, lastName, timeZone, selectedOrgGuids, roleType } = this.currentValues
      this.$apollo.mutate({
        mutation: editUserMutation,
        variables: {
          guid: this.userGuid,
          orgGuids: selectedOrgGuids,
          firstName,
          lastName,
          timeZone,
          roleType
        }
      }).then(({ data }) => {
        if (data.editUser.response) {
          const reload = snEvaultService.checkForSelfReload([this.userGuid], this.loggedInUser)
          this.setSuccessToast({ description: `Updated user successfully${reload ? '. Applying...' : ''}` })
          this.$emit('save')
          if (!reload) {
            this.saveBtnLoading = false
          }
        } else {
          this.setErrorToastType({ description: data.editUser.error })
          this.saveBtnLoading = false
        }
      }).catch(err => {
        this.setErrorToastType({ description: err })
        this.saveBtnLoading = false
      })
    },
    forcePasswordReset () {
      this.passwordResetLoading = true
      this.$apollo
        .query({
          query: updateUsersForcePasswordReset,
          fetchPolicy: 'no-cache',
          variables: { guids: [this.userGuid] }
        })
        .then(({ data }) => {
          if (data.updateUsersForcePasswordReset?.response) {
            this.setSuccessToast({ description: 'Password reset has been initiated.' })
            this.showPasswordResetModal = false
          } else if (data.updateUsersForcePasswordReset?.error) {
            this.setErrorToastType({ description: data.updateUsersForcePasswordReset?.error })
          }
        }).catch((error) => {
          this.setErrorToastType({ description: error.message })
        }).finally(() => { this.passwordResetLoading = false })
    }
  }
}
</script>

<style scoped>

.user-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 32px;
  margin-top: 24px;
}

.user-orgs {
  margin-top: 24px;
}

.contact-label {
  display: block;
  margin-bottom: 8px;
}

.user-actions-links {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

@media (max-width: 768px) {
  .user-grid {
    grid-template-columns: 1fr;
  }
}

</style>
